import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { itemDaBaseSchema, type ItemDaBaseEntrada, type StatusNaBase, type TipoNaBase } from "@bookdental/schemas";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { SeloDeStatus } from "./SeloDeStatus";
import { ROTULO_DO_TIPO } from "./rotulos";
import { CamposInsumo } from "./campos/CamposInsumo";
import { CamposCor, CamposEquipamento, CamposMaterial, CamposQuimica, CamposServico } from "./campos/CamposDoItem";
import { CamposEspecialidade, CamposProcedimento } from "./campos/CamposDaClinica";
import type { OpcoesDaBase } from "./campos/comum";

const NOVO: { [K in TipoNaBase]: Extract<ItemDaBaseEntrada, { tipo: K }> } = {
  servicos: { tipo: "servicos", nome: "", categoria: "", abrangencia: "DENTE", perfis: [], materialIds: [] },
  materiais: { tipo: "materiais", nome: "", materialCategoryId: "", perfis: [] },
  insumos: {
    tipo: "insumos", nome: "", tipoDeInsumo: "PRODUTO", fabricante: "", materialCategoryId: "",
    formato: "", translucidez: null, unidade: "UN", categoriaSugerida: "", perfis: [], usadoComIds: [], equipamentoIds: [],
  },
  quimicas: { tipo: "quimicas", nome: "", familia: null },
  cores: { tipo: "cores", escalaId: "", cor: "" },
  equipamentos: { tipo: "equipamentos", nome: "", tipoDeEquipamento: "", perfis: [], quimicaIds: [] },
  procedimentos: { tipo: "procedimentos", nome: "", especialidadeId: "", abrangencia: "BOCA", codigoTuss: null, unidade: null, duracaoMinutos: 30, cor: null, especialidadeIds: [] },
  especialidades: { tipo: "especialidades", nome: "", destaque: false },
};

/** Painel lateral de curadoria: item novo (id nulo) ou existente. */
export function EditorDaBase({
  tipo,
  id,
  status,
  aberto,
  onFechar,
  inicial,
  ligarItens,
}: {
  tipo: TipoNaBase;
  id: string | null;
  status?: StatusNaBase;
  aberto: boolean;
  onFechar: () => void;
  /** Item novo já preenchido (candidato). */
  inicial?: ItemDaBaseEntrada;
  /** Promover candidato: ao salvar, liga estes itens dos laboratórios ao item novo. */
  ligarItens?: string[];
}) {
  const utils = trpc.useUtils();
  const opcoes = trpc.admin.base.opcoes.useQuery(undefined, { enabled: aberto });
  const existente = trpc.admin.base.obter.useQuery({ tipo, id: id ?? "" }, { enabled: aberto && !!id });
  const [item, setItem] = useState<ItemDaBaseEntrada>(NOVO[tipo]);
  useEffect(() => {
    if (!aberto) return;
    setItem(id ? (existente.data ?? NOVO[tipo]) : (inicial ?? NOVO[tipo]));
  }, [aberto, id, tipo, existente.data, inicial]);

  const atualizarListas = () => {
    utils.admin.base.listar.invalidate();
    utils.admin.base.resumo.invalidate();
    utils.admin.base.opcoes.invalidate();
    if (id) utils.admin.base.obter.invalidate({ tipo, id });
  };
  const salvar = trpc.admin.base.salvar.useMutation({
    onSuccess: () => {
      atualizarListas();
      toast.success(id ? "Item atualizado." : "Item criado como rascunho.");
      onFechar();
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar."),
  });
  const promover = trpc.admin.base.promover.useMutation({
    onSuccess: ({ ligados }) => {
      atualizarListas();
      utils.admin.base.candidatos.invalidate();
      toast.success(`Candidato promovido a rascunho na base; ${ligados} ${ligados === 1 ? "item ligado" : "itens ligados"}.`);
      onFechar();
    },
    onError: (e) => toast.error(e.message || "Não foi possível promover."),
  });
  const mudarStatus = trpc.admin.base.mudarStatus.useMutation({
    onSuccess: (_r, v) => {
      atualizarListas();
      toast.success(v.status === "PUBLICADO" ? "Item publicado." : v.status === "ARQUIVADO" ? "Item arquivado." : "Item voltou a rascunho.");
      onFechar();
    },
    onError: (e) => toast.error(e.message || "Não foi possível mudar o status."),
  });

  const mudar = (parcial: object) => setItem((atual) => ({ ...atual, ...parcial }) as ItemDaBaseEntrada);
  const valido = itemDaBaseSchema.safeParse(item);
  const carregando = opcoes.isLoading || (!!id && existente.isLoading);

  return (
    <Sheet open={aberto} onOpenChange={(o) => !o && onFechar()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden border-l border-zinc-200 bg-white p-0 shadow-xl sm:max-w-xl">
        <SheetHeader className="border-b border-admin-200 px-6 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-admin-900">
            {ligarItens ? "Promover" : id ? "Editar" : "Novo"} · {ROTULO_DO_TIPO[tipo].toLowerCase()}
            {status && <SeloDeStatus status={status} />}
          </SheetTitle>
          <SheetDescription>Mudanças valem para quem escolher o item daqui pra frente; cópias já feitas não mudam.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {carregando || !opcoes.data ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-admin-700" />
            </div>
          ) : (
            <Campos item={item} mudar={mudar} opcoes={opcoes.data} />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-admin-200 px-6 py-3">
          {id && status !== "PUBLICADO" && (
            <BotaoStatus onClick={() => mudarStatus.mutate({ tipo, ids: [id], status: "PUBLICADO" })} disabled={mudarStatus.isPending}>
              Publicar
            </BotaoStatus>
          )}
          {id && status !== "ARQUIVADO" && (
            <BotaoStatus onClick={() => mudarStatus.mutate({ tipo, ids: [id], status: "ARQUIVADO" })} disabled={mudarStatus.isPending}>
              Arquivar
            </BotaoStatus>
          )}
          {id && status === "ARQUIVADO" && (
            <BotaoStatus onClick={() => mudarStatus.mutate({ tipo, ids: [id], status: "RASCUNHO" })} disabled={mudarStatus.isPending}>
              Voltar a rascunho
            </BotaoStatus>
          )}
          <button
            type="button"
            onClick={() => {
              if (!valido.success) return;
              if (ligarItens) promover.mutate({ item: valido.data, itemIds: ligarItens });
              else salvar.mutate(valido.data);
            }}
            disabled={!valido.success || salvar.isPending || promover.isPending}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md bg-admin-900 px-4 text-sm font-semibold text-white hover:bg-admin-700 disabled:opacity-50"
          >
            {(salvar.isPending || promover.isPending) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {ligarItens ? "Promover para a base" : "Salvar"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BotaoStatus(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className="h-9 rounded-md border border-admin-200 px-3 text-sm font-medium text-admin-900 hover:bg-admin-50 disabled:opacity-50"
    />
  );
}

function Campos({ item, mudar, opcoes }: { item: ItemDaBaseEntrada; mudar: (p: object) => void; opcoes: OpcoesDaBase }) {
  switch (item.tipo) {
    case "servicos":
      return <CamposServico item={item} mudar={mudar} opcoes={opcoes} />;
    case "materiais":
      return <CamposMaterial item={item} mudar={mudar} opcoes={opcoes} />;
    case "insumos":
      return <CamposInsumo item={item} mudar={mudar} opcoes={opcoes} />;
    case "quimicas":
      return <CamposQuimica item={item} mudar={mudar} opcoes={opcoes} />;
    case "cores":
      return <CamposCor item={item} mudar={mudar} opcoes={opcoes} />;
    case "equipamentos":
      return <CamposEquipamento item={item} mudar={mudar} opcoes={opcoes} />;
    case "procedimentos":
      return <CamposProcedimento item={item} mudar={mudar} opcoes={opcoes} />;
    case "especialidades":
      return <CamposEspecialidade item={item} mudar={mudar} />;
  }
}
